import { GraphQLFormattedError } from 'graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from './useToast';
import { ApolloError, gql } from '@apollo/client';
import {
	UseContacts_ContactFragment,
	useUseContacts_CreateContactAsVendorMutation,
	useUseContacts_GetContactsWhereLazyQuery,
	useUseContacts_UpdateContactMutation,
	CreateContactDataInput,
	CustomContactUpdateInput,
	SortOrderType,
	UserType,
} from '#types';
import { DeepOmit, filterTypenameFromObject } from '../lib/helpers/apollo';
import { useSelector } from 'react-redux';
import { FlattenedUser } from '../types/user';
import rpcShared from '@rockpapercoin/rpc-shared';

export const contactFragment = gql`
	fragment useContacts_contact on Contact {
		id
		firstName
		lastName
		eventDate
		venue
		phone
		note
		status
		companyName
		source
		hasCustomerIntent
		customer {
			id
			email
		}
		assignedMember {
			id
		}
		vendor {
			id
		}
	}
`;

export const contactOnSignatureFragment = gql`
	fragment useContacts_contactOnSignature on CustomerSignatureContact {
		id
		status
		firstName
		lastName
		eventDate
		venue
		phone
		note
		companyName
		hasCustomerIntent
		customer {
			id
			email
		}
		assignedMember {
			id
		}
		vendor {
			id
		}
	}
`;

// omitting typename from the type here lets us better handle Contact and CustomerSignatureContact types

export type Contact = DeepOmit<
	Omit<
		UseContacts_ContactFragment,
		'eventDate' | 'note' | 'companyName' | 'source' | 'venue' | 'phone'
	>,
	'__typename'
> &
	Pick<UseContacts_ContactFragment, 'eventDate' | 'venue' | 'phone'> & {
		note?: string | null;
		companyName?: string | null;
		source?: string | null;
	};
export type NewContact = Partial<
	Pick<
		Contact,
		| 'id'
		| 'firstName'
		| 'lastName'
		| 'eventDate'
		| 'status'
		| 'companyName'
		| 'source'
	>
> & {
	customer?: {
		id?: Contact['customer']['id'];
		email?: Contact['customer']['email'];
	};
	note?: string | null;
};

export type Contacts = Array<Contact>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getContactsWhere = gql`
	query useContacts_getContactsWhere(
		$orderBy: [ContactOrderByInput]
		$skip: Int
		$take: Int
		$where: ContactWhereInput!
	) {
		getContactsWhere(
			orderBy: $orderBy
			skip: $skip
			take: $take
			where: $where
		) {
			contacts {
				...useContacts_contact
			}
		}
	}
	${ contactFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateContactMutation = gql`
	mutation useContacts_updateContact(
		$data: CustomContactUpdateInput!
		$where: ContactWhereUniqueInput!
	) {
		updateContact(data: $data, where: $where) {
			...useContacts_contact
		}
	}
	${ contactFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createContactAsVendorMutation = gql`
	mutation useContacts_createContactAsVendor(
		$data: CreateContactDataInput!
		$where: UserByEmailUniqueInput!
	) {
		createContactAsVendor(data: $data, where: $where) {
			...useContacts_contact
		}
	}
	${ contactFragment }
`;

/**
 * A simple hook to fetch, update, and maintain a list of contacts
 */
const useContacts = ( organizationId?: string, throwErrors = false ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const { showError } = useToast();
	const [ contacts, setContacts ] = useState<Contacts | undefined>();
	// Have we finished our initial load of contacts for this vendor?
	const [ isLoaded, setIsLoaded ] = useState( false );
	// Are we currently "in flight" on a contact creation or update?
	const [ isReady, setIsReady ] = useState( false );
	const [ getContactsWhere, { loading: gettingContacts } ] =
		useUseContacts_GetContactsWhereLazyQuery();
	const [ updateContact, { loading: updatingContact } ] =
		useUseContacts_UpdateContactMutation();
	const [ createContactAsVendor, { loading: creatingContact } ] =
		useUseContacts_CreateContactAsVendorMutation();
	// helper to either throw errors or show them, so caller can filter errors if desired
	const handleErrors = useCallback(
		( errors?: ApolloError | readonly GraphQLFormattedError[] ) => {
			if ( errors ) {
				if ( throwErrors ) {
					throw errors;
				}
				if ( Array.isArray( errors ) ) {
					errors.forEach( ( e ) => showError( e ) );
				} else {
					showError( errors );
				}
			}
		},
		[ throwErrors, showError ]
	);
	// Fetch all of the contacts for the vendor once and store them client-side while using this hook
	useEffect( () => {
		const fetchContactsOnMount = async () => {
			const userIsNonAdminAssignedMember =
				user?.isLoggedIn &&
				user?.userType === UserType.OrgUser &&
				!rpcShared.organizations.isAdminOrManager( user.organization, user ) &&
				user.organization.id === organizationId;
			if ( organizationId ) {
				const response = await getContactsWhere( {
					variables: {
						where: {
							...( userIsNonAdminAssignedMember
								? { assignedMember: { id: user.id } }
								: { vendor: { id: organizationId } } ),
						},
						orderBy: [ { createdAt: SortOrderType.desc } ],
					},
				} );
				if ( response.error ) {
					handleErrors( response.error );
				} else if ( response.data?.getContactsWhere ) {
					setContacts( response.data?.getContactsWhere.contacts );
				} else {
					setContacts( undefined );
				}
				setIsLoaded( ( prevState ) => ( !prevState ? true : prevState ) );
				setIsReady( ( prevState ) => ( !prevState ? true : prevState ) );
			}
		};
		fetchContactsOnMount();
	}, [
		organizationId,
		handleErrors,
		setIsReady,
		setContacts,
		setIsLoaded,
		getContactsWhere,
		user,
	] );

	// Keys we can update
	const updatableContactKeys = useMemo(
		() => [
			'firstName',
			'lastName',
			'eventDate',
			'phone',
			'companyName'
		],
		[]
	);

	const contactDataIsValidForUpdate = (
		contact: Partial<CustomContactUpdateInput>
	): contact is CustomContactUpdateInput =>
		!!contact.firstName && !!contact.lastName && !!contact.email;

	// Reducer we can use to create the updateArgs for API.updateContact
	const updateContactReducer = useCallback(
		(
			foundContact: Contact,
			newContact: NewContact
		): CustomContactUpdateInput | undefined => {
			const data: Record<string, any> = Object.entries( newContact ).reduce(
				( accumulator, [ key, value ] ) =>
					updatableContactKeys.includes( key ) && value !== undefined
						? { ...accumulator, [ key ]: value }
						: accumulator,
				{ firstName: foundContact.firstName, lastName: foundContact.lastName }
			);
			data.email = newContact.customer?.email;
			if ( contactDataIsValidForUpdate( data ) ) {
				return data;
			}
		},
		[ updatableContactKeys ]
	);

	const isContact = ( contact: Contact | NewContact ): contact is Contact =>
		'id' in contact;

	const findContact = useCallback(
		( contact: Contact ): Contact | undefined =>
			contacts?.find( ( c ) => c.customer.email === contact.customer?.email ),
		[ contacts ]
	);

	const filterContacts = useCallback(
		( text?: string ): Contacts | undefined =>
			contacts?.filter( ( c ) =>
				text
					? c.firstName.toLowerCase().includes( text.toLowerCase() ) ||
					  c.lastName.toLowerCase().includes( text.toLowerCase() ) ||
					  c.customer.email.toLowerCase().includes( text.toLowerCase() ) ||
					  c.companyName?.toLowerCase().includes( text.toLowerCase() )
					: undefined
			),
		[ contacts ]
	);

	const updateContactInState = useCallback(
		/**
		 * This is a utility function not exposed outside of this useContacts hook, intended
		 * to look in hook-state to first see if the contact exists and if found update that record in state,
		 * and if it's NOT found insert the new record. New contacts always go at the beginning of the list
		 * since it's sorted by most recent first.
		 * is preserved after addition/modification
		 */
		( contact: Contact ) => {
			const foundContact = findContact( contact );
			if ( foundContact ) {
				setContacts( ( prevState ) =>
					prevState?.map( ( c ) =>
						c.customer.email === contact.customer.email ? contact : c
					)
				);
			} else if ( contact ) {
				setContacts( ( prevState ) => [ contact, ...( prevState ? prevState : [] ) ] );
			}
			return contact;
		},
		[ setContacts, findContact ]
	);

	const contactIsCreateContactDataInput = (
		contact?: Partial<CreateContactDataInput>
	): contact is CreateContactDataInput =>
		!!contact?.firstName && !!contact?.lastName;

	// This is passed a contact, updates it in the BE, stores the result in this hook, and returns it
	const getContact = useCallback(
		async ( contact: Contact | NewContact ) => {
			if ( !isLoaded || !isReady ) {
				return false;
			}
			// Does the selected contact exist already?
			const foundContact = isContact( contact )
				? filterTypenameFromObject( findContact( contact ) )
				: undefined;
			if ( foundContact ) {
				// update the existing contact

				const data: CustomContactUpdateInput | undefined = updateContactReducer(
					foundContact,
					contact
				);
				if ( data ) {
					setIsReady( false );
					/* if they just typed in info but the email pre-existed there'll be no id on contact, so
				get the ID from the record we have from foundContact.id */
					const response = await updateContact( {
						variables: {
							where: { id: foundContact.id },
							data,
						},
					} );
					if ( response.errors || !response.data ) {
						let sharedClientDetected = false;
						const remainingErrors = rpcShared.errorFiltering.filterApiErrors(
							response,
							[ 'cannotUpdateEmailForGuestWithOtherContacts', 'cannotUpdateEmailForClient', ],
							{
								cannotUpdateEmailForGuestWithOtherContacts: () => {
									sharedClientDetected = true;
								},
							}
						);
						if ( sharedClientDetected ) {
							// The contact's customer is a registered user shared across orgs —
							// updateContact cannot change their email. Fall back to
							// createContactAsVendor, which uses email only as a lookup key
							// and creates or re-links the contact without touching the user's email.
							const fallbackResponse = await createContactAsVendor( {
								variables: {
									where: { email: data.email },
									data: {
										firstName: data.firstName,
										lastName: data.lastName,
										...( data.phone ? { phone: data.phone } : {} ),
										...( data.eventDate ? { eventDate: data.eventDate } : {} ),
										...( data.companyName
											? { companyName: data.companyName }
											: {} ),
										...( data.venue ? { venue: data.venue } : {} ),
										...( data.source ? { source: data.source } : {} ),
									},
								},
							} );
							if ( fallbackResponse.errors || !fallbackResponse.data ) {
								handleErrors( fallbackResponse.errors );
								setIsReady( true );
								return false;
							} else {
								const newContact = fallbackResponse.data.createContactAsVendor;
								// Replace the old contact by its original ID so the new contact
								// doesn't get prepended as a duplicate when IDs differ.
								setContacts( ( prevState ) =>
									prevState?.map( ( c ) =>
										c.id === foundContact.id ? newContact : c
									)
								);
								setIsReady( true );
								return newContact;
							}
						}
						handleErrors( remainingErrors.errors );
						setIsReady( true );
						return false;
					} else {
						setIsReady( true );
						return updateContactInState( response.data.updateContact );
					}
				}
				return false;
			} else {
				// We didn't select an existing contact, so make one
				const email = contact.customer?.email;
				const data: Partial<CreateContactDataInput> = {
					firstName: contact.firstName,
					lastName: contact.lastName,
					companyName: contact.companyName,
				};
				if ( !contactIsCreateContactDataInput( data ) || !email ) {
					return false;
				}
				setIsReady( false );
				const response = await createContactAsVendor( {
					variables: {
						where: { email },
						data,
					},
				} );
				if ( response.errors || !response.data ) {
					setIsReady( true );
					handleErrors( response.errors );
					return false;
				} else {
					setIsReady( true );
					return updateContactInState( response.data.createContactAsVendor );
				}
			}
		},
		[
			createContactAsVendor,
			updateContact,
			handleErrors,
			findContact,
			updateContactInState,
			updateContactReducer,
			isLoaded,
			isReady,
			setIsReady,
		]
	);

	return {
		loading: gettingContacts || updatingContact || creatingContact,
		isLoaded,
		isReady,
		contacts,
		findContact,
		filterContacts,
		getContact,
	};
};

export default useContacts;
